import { ReactElement, RefObject, useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
  type ElementDropTargetEventBasePayload,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import type { NodeType, ReorderRequest, RowOrGroup } from '../types';
import { DataGridHandle } from 'components/DataGrid';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { createRoot } from 'react-dom/client';
import type { Availability, Instruction, Operation } from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item';
import { attachInstruction, extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item';
import { collapsedGroupsAtom, usePlanMutations } from '../state';
import { useSetAtom } from 'jotai';
import { SupplierTitle } from 'modules/supplier/supplier/components';
import { dragPreview } from './columns/style.css';
import clsx from 'clsx';
import { MAX_GROUP_DEPTH } from '../const';
type Position = ReorderRequest['position'];

type ItemData = {
  _key: string;
  id: number;
  subjectType: NodeType;
  isExpanded: boolean;
  groupDepth: number;
  groupHeight: number;
};

type Draggable = {
  handleRef: RefObject<HTMLButtonElement>;
  containerRef: RefObject<HTMLDivElement>;
  isDragging: boolean;
  instruction: Instruction | null;
};

const operationMap: Record<Operation, Position> = {
  combine: 'INSIDE',
  'reorder-before': 'ABOVE',
  'reorder-after': 'BELOW',
};

function renderPreview(row: RowOrGroup): ReactElement {
  if (row._isGroup) {
    return <div className={clsx(dragPreview, 'group')}>{row.title || `Unnamed group ${row.id}`}</div>;
  }
  return (
    <div className={clsx(dragPreview, 'row')}>
      {row.supplier ? <SupplierTitle {...row.supplier} /> : `Missing supplier ${row.id}`}
    </div>
  );
}

function getOperation(dropTarget: ItemData, dragSource: ItemData): Record<Operation, Availability> {
  const isDraggingGroup = dragSource.subjectType === 'GROUP';
  const isTargetGroup = dropTarget.subjectType === 'GROUP';

  // Calculate depth issues (only relevant if dragging a group)
  const isTooDeep = dropTarget.groupDepth + dragSource.groupHeight > MAX_GROUP_DEPTH;

  // Define when depth blocks reordering (Dragging Group -> Non-Group Target)
  const isDepthBlocked = isDraggingGroup && !isTargetGroup && isTooDeep;

  return {
    // Combine:
    // 1. Target is NOT a group -> 'not-available' (Universal rule)
    // 2. Target IS a group, but result is too deep -> 'blocked' (Group only)
    // 3. Otherwise -> 'available'
    combine: !isTargetGroup ? 'not-available' : isDraggingGroup && isTooDeep ? 'blocked' : 'available',

    // Reorder Before:
    // Blocked if dragging a group near a non-group causes depth issues
    'reorder-before': isDepthBlocked ? 'blocked' : 'available',

    // Reorder After:
    // 1. Target is Expanded -> 'not-available' (Highest Precedence)
    // 2. Depth issue -> 'blocked'
    // 3. Otherwise -> 'available'
    'reorder-after':
      isTargetGroup && dropTarget.isExpanded ? 'not-available' : isDepthBlocked ? 'blocked' : 'available',
  };
}

export function useDraggable(row: RowOrGroup): Draggable {
  const { _key, id, _isGroup, isExpanded, groupHeight, groupDepth } = row;
  const subjectType = _isGroup ? 'GROUP' : row.kind;
  const setCollapsed = useSetAtom(collapsedGroupsAtom);
  const [instruction, setInstruction] = useState<Instruction | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const handleRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: renderPreview is fine
  useEffect(() => {
    invariant(handleRef.current);
    invariant(containerRef.current);

    function onChange({ self }: ElementDropTargetEventBasePayload) {
      setInstruction(extractInstruction(self.data));
    }

    const data: ItemData = {
      _key,
      id,
      subjectType,
      isExpanded,
      groupDepth,
      groupHeight,
    };

    return combine(
      draggable({
        element: handleRef.current,
        getInitialData: () => data,
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            getOffset: pointerOutsideOfPreview({ x: '44px', y: '8px' }),
            render: ({ container }) => {
              const root = createRoot(container);
              root.render(renderPreview(row));
              return () => root.unmount();
            },
            nativeSetDragImage,
          });
        },
        onDragStart: ({ source }) => {
          setIsDragging(true);
          // collapse if is expanded
          const sourceData = source.data as ItemData;
          if (sourceData.isExpanded) {
            setCollapsed((current) => {
              const newSet = new Set(current);
              newSet.add(sourceData._key);
              return newSet;
            });
          }
        },
        onDrop: ({ source }) => {
          setIsDragging(false);
          // expand if was collasped
          const sourceData = source.data as ItemData;
          if (sourceData.isExpanded) {
            setCollapsed((current) => {
              const newSet = new Set(current);
              newSet.delete(sourceData._key);
              return newSet;
            });
          }
        },
      }),
      // @TODO expand groups on drag over after timeout?
      dropTargetForElements({
        element: containerRef.current,
        getData: ({ input, element, source }) => {
          return attachInstruction(data, {
            input,
            element,
            operations: getOperation(data, source.data as ItemData),
            axis: 'vertical',
          });
        },

        onDragEnter: onChange,
        onDrag: onChange,
        onDragLeave: () => {
          setInstruction(null);
        },
        canDrop: ({ source }) => {
          const src = source.data as ItemData;
          // interactions with self
          if (src.id === data.id && src.subjectType === data.subjectType) {
            return false;
          }
          return true;
        },
        onDrop: () => {
          setInstruction(null);
        },
      }),
    );
  }, [_key, id, subjectType, isExpanded, groupDepth, groupHeight, setCollapsed]);
  return {
    handleRef,
    containerRef,
    isDragging,
    instruction,
  };
}

export function useDragMonitor(gridRef: RefObject<DataGridHandle>) {
  const { reorder, selectCell } = usePlanMutations();

  // biome-ignore lint/correctness/useExhaustiveDependencies: move and refs
  useEffect(() => {
    invariant(gridRef.current);
    const element = gridRef.current.element as HTMLElement;
    return combine(
      autoScrollForElements({ element, getAllowedAxis: () => 'vertical' }),
      monitorForElements({
        onDrag({ source }) {
          const sourceData = source.data as ItemData;
          selectCell(sourceData._key, 'title', false, false);
        },
        onDrop({ source, location }) {
          // setIsDragging(null);
          const target = location.current.dropTargets[0];
          if (!target) {
            // if dropped outside of any drop targets
            return;
          }
          const sourceData = source.data as ItemData;
          const targedData = target.data as ItemData;
          const instruction = extractInstruction(targedData);

          if (instruction && !instruction.blocked) {
            reorder({
              subjectId: sourceData.id,
              subjectType: sourceData.subjectType,
              targetId: targedData.id,
              targetType: targedData.subjectType,
              position: operationMap[instruction.operation],
            });
          }
        },
      }),
    );
  }, [reorder]);
}
